import { styled } from '@mui/material';
import React, { useState } from 'react';
import { useAppConfigContext } from '@/context/AppConfig';
import { useAppConstantsContext } from '@/context/AppConstants';
import { SectionBaseProps } from '@/reports/contact/Interfaces';
import { RowCard } from '@/reports/contact/RowCard';
import { ShowMoreToggle, SocialProfileSvg } from '@/reports/contact/components';
import { ReportSection, RestoreAllDataButton } from '@/reports/index';
import { FlexContainer } from '@/reports/shared/OverviewItems/components';
import { peopleTypes } from '@/reports/types/index';
import { Link, Stack } from '@/theme/index';

const DataContainer = styled(FlexContainer)(({ theme }) => ({
  alignItems: 'flex-start',
  overflow: 'auto',
  width: '85%',

  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

const TextContainer = styled('div')(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(1.25),
  },
}));

interface SocialRowProps {
  profile: peopleTypes.Profile;
  permalink: string;
}

/**
 * This component displays an social profile individually
 */
function SocialRow({ profile, permalink }: SocialRowProps) {
  const { trackEvent } = useAppConfigContext();

  const trackProfile = () => {
    if (profile.domain) {
      trackEvent(
        `social profile ${profile?.domain.replace(/\.[a-zA-Z]+$/, '')}`,
        'engagement click',
        'contact report'
      );
    }
  };

  return (
    <RowCard
      permalink={permalink}
      item={profile}
      value={profile.url || ''}
      rowStyles={{ paddingLeft: 0 }}
      component={
        <DataContainer>
          <TextContainer>
            <Stack
              spacing={1}
              direction="row"
              alignItems="center"
              onClick={trackProfile}
              sx={(theme) => ({
                mr: theme.spacing(0.5),
              })}
            >
              <FlexContainer>
                <SocialProfileSvg site={profile.domain || ''} />
              </FlexContainer>
              <Link
                sx={{ cursor: 'pointer' }}
                target="_blank"
                href={profile.url ? profile.url : ''}
                style={{ textDecoration: 'none' }}
              >
                {profile.url?.replace(/^https?:\/\//, '')}
              </Link>
            </Stack>
          </TextContainer>
        </DataContainer>
      }
    />
  );
}

interface SocialRowsProps extends SectionBaseProps {
  profilesList: peopleTypes.Profiles;
}

/**
 * This component displays a list of the social profiles that are shown in the report
 */
function SocialRows({ permalink, profilesList }: SocialRowsProps): JSX.Element {
  return (
    <>
      {profilesList.map((profile) => (
        <SocialRow key={profile.url} profile={profile} permalink={permalink} />
      ))}
    </>
  );
}

const SocialThreshold = 5;

function socialProfilesList(
  expand: boolean,
  socialList: peopleTypes.Profiles
): peopleTypes.Profiles {
  return expand ? socialList : socialList.slice(0, SocialThreshold);
}

export interface ContactSocialSectionProps extends SectionBaseProps {
  profiles: peopleTypes.Profiles;
}

export function ContactSocialSection({
  personName,
  profiles,
  permalink,
}: ContactSocialSectionProps) {
  const {
    data: { brandName },
  } = useAppConstantsContext();

  // Show More Setters
  const [expandProfiles, setExpandProfiles] = useState(false);

  const expandProfilesHandler = () => {
    setExpandProfiles(!expandProfiles);
  };

  const sortedProfiles = socialProfilesList(expandProfiles, profiles);

  return (
    <ReportSection
      title="Possible Social Media"
      sectionDescription={`${brandName} runs a comprehensive search of the internet to uncover photos, social media profiles, blogs and websites that may potentially be associated with ${personName}.`}
      sectionId="social-media-section"
      hasContent={sortedProfiles.length > 0}
      personName={personName}
    >
      <SocialRows profilesList={sortedProfiles} permalink={permalink} />

      <ShowMoreToggle
        list={sortedProfiles}
        expand={expandProfiles}
        expandHandler={expandProfilesHandler}
      />

      <RestoreAllDataButton items={sortedProfiles} permalink={permalink} />
    </ReportSection>
  );
}
